import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  isEmailDispatch,
  summarizeEmailDispatch,
  type ContentPublishingConnector,
  type ContentPublishingDispatch,
} from "@/lib/composerCore";
import {
  parsePostSendEvents,
  useRecipientRows,
  useSuppressionDetails,
} from "./dispatchDrawer/dispatchDrawerData";
import { EmailDispatchSection } from "./dispatchDrawer/EmailDispatchSection";
import { WebhookDispatchSection } from "./dispatchDrawer/WebhookDispatchSection";
import { PendingMediaChecklist } from "./PendingMediaChecklist";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dispatch: ContentPublishingDispatch | null;
  connector: ContentPublishingConnector | null;
  publicationTitle?: string | null;
  onResendToFailed?: (dispatch: ContentPublishingDispatch, recipients: string[]) => void;
  resending?: boolean;
  canResend?: boolean;
};

export const DispatchDetailDrawer = ({
  open,
  onOpenChange,
  dispatch,
  connector,
  publicationTitle,
  onResendToFailed,
  resending,
  canResend,
}: Props) => {
  const isEmail = dispatch ? isEmailDispatch(dispatch) : false;
  const request = useMemo(
    () => (dispatch?.request_snapshot ?? {}) as Record<string, unknown>,
    [dispatch],
  );
  const response = useMemo(
    () => (dispatch?.response_snapshot ?? {}) as Record<string, unknown>,
    [dispatch],
  );
  const statusCode = typeof response.status_code === "number" ? response.status_code : null;
  const emailSummary = dispatch && isEmail ? summarizeEmailDispatch(dispatch) : null;
  const postSendEvents = useMemo(() => parsePostSendEvents(response), [response]);
  const recipientRows = useRecipientRows(emailSummary, postSendEvents);
  const { suppressionByEmail, suppressionLoading } = useSuppressionDetails(open, dispatch, recipientRows);

  const handleCloseAndOpenConnector = (connectorId: string) => {
    onOpenChange(false);
    // Defer so the sheet starts closing before we dispatch — gives
    // ConnectorManager a frame to mount its scroll target if it was offscreen.
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("excerptus:open-connector-edit", {
          detail: { connectorId },
        }),
      );
    }, 50);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Dispatch detail</SheetTitle>
          <SheetDescription>
            {isEmail
              ? "Sanitized record of recipients, subject, and provider response."
              : "Sanitized request and response snapshots. Secret header values are never recorded."}
          </SheetDescription>
        </SheetHeader>

        {!dispatch ? (
          <p className="mt-6 text-sm text-muted-foreground">No dispatch selected.</p>
        ) : (
          <div className="mt-4 space-y-4 text-sm">
            <div className="space-y-2 rounded-md border border-border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={
                    dispatch.status === "succeeded"
                      ? "secondary"
                      : dispatch.status === "failed"
                        ? "destructive"
                        : "outline"
                  }
                  className="capitalize"
                >
                  {dispatch.status}
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {connector?.connector_type ?? (isEmail ? "email" : "webhook")}
                </Badge>
                {!isEmail && statusCode !== null ? (
                  <span className="text-xs text-muted-foreground">HTTP {statusCode}</span>
                ) : null}
                {isEmail && emailSummary ? (
                  <span className="text-xs text-muted-foreground">
                    {emailSummary.acceptedCount} accepted · {emailSummary.rejectedCount} rejected
                  </span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(dispatch.created_at).toLocaleString()}
                </span>
              </div>
              <div className="grid gap-1 text-xs text-muted-foreground">
                <p>
                  <span className="text-foreground">Connector:</span> {connector?.display_name ?? "Removed connector"}
                </p>
                {publicationTitle ? (
                  <p>
                    <span className="text-foreground">Publication:</span> {publicationTitle}
                  </p>
                ) : null}
                {dispatch.error_message ? (
                  <p className="text-destructive">{dispatch.error_message}</p>
                ) : null}
              </div>
            </div>

            {isEmail && emailSummary ? (
              <EmailDispatchSection
                dispatch={dispatch}
                emailSummary={emailSummary}
                recipientRows={recipientRows}
                suppressionByEmail={suppressionByEmail}
                suppressionLoading={suppressionLoading}
                onResendToFailed={onResendToFailed}
                resending={resending}
                canResend={canResend}
              />
            ) : (
              <WebhookDispatchSection
                dispatch={dispatch}
                connector={connector}
                request={request}
                response={response}
                onCloseAndOpenConnector={handleCloseAndOpenConnector}
              />
            )}

            {!isEmail && dispatch.publication_id ? (
              <PendingMediaChecklist
                scope="publication"
                workspaceId={dispatch.workspace_id}
                parentId={dispatch.publication_id}
                dispatchId={dispatch.id}
              />
            ) : null}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DispatchDetailDrawer;
